import dayjs from 'dayjs'
import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Formik, Form } from 'formik'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import LoadingButton from '@mui/lab/LoadingButton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'

import AutocompleteField from '@/components/formik/fields/AutocompleteField'
import DatePickerField from '@/components/formik/fields/DatePickerField'
import { AutocompleteOption } from '@/components/ui/inputs/AutocompleteInput'

export type ExpertisesSearchFormValues = {
  doctor: string
  patient: string
  type: string
  startDate: {
    $gte: Date | null
    $lte: Date | null
  }
}

export type ExpertisesSearchFormProps = {
  direction?: 'column' | 'row'
  doctorOptions: AutocompleteOption[]
  onReset?: (values: ExpertisesSearchFormValues) => void
  onSearchDoctor?: (value: string) => void
  onSearchPatient?: (value: string) => void
  onSubmit: (values: ExpertisesSearchFormValues) => void
  patientOptions: AutocompleteOption[]
}

export default function ExpertisesSearchForm(props: ExpertisesSearchFormProps) {
  const { direction = 'column', doctorOptions, onReset, onSearchDoctor, onSearchPatient, onSubmit, patientOptions } = props
  const { t } = useTranslation()

  const theme = useTheme()
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'))

  const handleSearchDoctor = useCallback(
    (inputValue: string) => {
      onSearchDoctor?.(inputValue)
    },
    [onSearchDoctor]
  )

  const handleSearchPatient = useCallback(
    (inputValue: string) => {
      onSearchPatient?.(inputValue)
    },
    [onSearchPatient]
  )

  return (
    <Box sx={{ padding: '0 15px' }}>
      <Typography variant="h2" sx={{ mt: '30px', mb: '30px' }}>
        {t('page.expertises.expertisesSearchForm.title')}
      </Typography>
      <Formik
        initialValues={{
          doctor: '',
          patient: '',
          type: '',
          startDate: {
            $gte: dayjs().startOf('day').toDate(),
            $lte: null,
          },
        }}
        onSubmit={onSubmit}
      >
        {(formikProps) => (
          <Form onSubmit={formikProps.handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={isLargeScreen ? 12 : 6}>
                <AutocompleteField
                  label={t('page.expertises.expertisesSearchForm.field.searchByPatient.label')}
                  name="patient"
                  onInputChange={handleSearchPatient}
                  options={patientOptions}
                />
              </Grid>
              <Grid item xs={12} sm={isLargeScreen ? 12 : 6}>
                <AutocompleteField
                  label={t('page.expertises.expertisesSearchForm.field.searchByDoctor.label')}
                  name="doctor"
                  onInputChange={handleSearchDoctor}
                  options={doctorOptions}
                />
              </Grid>
              <Grid item xs={12} sm={isLargeScreen ? 12 : 6}>
                <DatePickerField fullWidth label={t('page.expertises.expertisesSearchForm.field.startDateFrom.label')} name="startDate.$gte" />
              </Grid>
              <Grid item xs={12} sm={isLargeScreen ? 12 : 6}>
                <DatePickerField fullWidth label={t('page.expertises.expertisesSearchForm.field.startDateTo.label')} name="startDate.$lte" />
              </Grid>
              <Grid item xs={12}>
                <Stack direction="row" justifyContent="end" spacing={1}>
                  <Button
                    onClick={() => {
                      formikProps.handleReset()
                      onReset?.(formikProps.initialValues)
                    }}
                    variant="neutral"
                  >
                    {t('common:button.reset.label')}
                  </Button>
                  <LoadingButton id="searchButton" type="submit" variant="contained">
                    {t('common:button.submit.label')}
                  </LoadingButton>
                </Stack>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </Box>
  )
}
